import { useEffect } from 'react';

const isScrollableInput = (el: EventTarget | null): el is HTMLElement => {
  if (!(el instanceof HTMLElement)) return false;
  if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
  if (el.tagName !== 'INPUT') return false;
  const type = (el as HTMLInputElement).type;
  return (
    type !== 'hidden' &&
    type !== 'button' &&
    type !== 'submit' &&
    type !== 'reset' &&
    type !== 'checkbox' &&
    type !== 'radio'
  );
};

/**
 * Listens for `focusin` while mounted and scrolls the focused input into the
 * center of its nearest scroll container. Needed because iOS Safari/Chrome's
 * auto "scroll focused field into view" only operates on the page (locked
 * while a modal is open), so the modal's own scroll container never moves and
 * the outside label gets clipped above the keyboard.
 *
 * Each input is scrolled at most ONCE per hook lifetime (e.g. once per modal
 * open). Heroui Autocomplete popovers can briefly remount and trigger an extra
 * `focusin` on the trigger input — we don't want to re-run the scroll
 * animation mid-typing.
 *
 * Use inside a component that mounts only while the modal is open so we don't
 * leak listeners or affect non-modal inputs.
 */
export const useScrollFocusedInputIntoView = (options?: { delayMs?: number }) => {
  const delay = options?.delayMs ?? 250;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const scrolled = new WeakSet<HTMLElement>();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!isScrollableInput(target)) return;
      if (scrolled.has(target)) return;
      scrolled.add(target);
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        try {
          target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch {
          target.scrollIntoView();
        }
      }, delay);
    };

    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [delay]);
};
